// API Configuration and Helper
export const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Refresh token 5 minutes before expiry
const REFRESH_MARGIN_MS = 300000;

// Singleton refresh: only one in-flight refresh at a time
let refreshPromise = null;
let refreshTimer = null;

// Diagnostic logging (visible in browser DevTools)
const log = (...args) => console.log('[LAWD Auth]', ...args);
const warn = (...args) => console.warn('[LAWD Auth]', ...args);

/**
 * Attempt to refresh the access token.
 * Singleton — concurrent callers share the same promise.
 * Tokens are now transported via httpOnly cookies, so no
 * manual storage or header management is needed.
 */
const refreshAccessToken = async () => {
  if (refreshPromise) {
    log('Refresh already in flight, waiting...');
    return refreshPromise;
  }

  log('Starting token refresh...');

  refreshPromise = (async () => {
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 3000, 5000];

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({})
        });

        if (!response.ok) {
          const errBody = await response.text().catch(() => '');
          warn(`Refresh HTTP ${response.status} (attempt ${attempt + 1}): ${errBody}`);

          // Only give up on explicit auth rejection (401/403)
          if (response.status === 401 || response.status === 403) {
            warn('Refresh token rejected by server — session dead');
            return false;
          }

          // Server error (500, 502, etc.) — retry
          if (attempt < MAX_RETRIES - 1) {
            log(`Server error — retrying in ${RETRY_DELAYS[attempt]}ms...`);
            await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt]));
            continue;
          }
          return false;
        }

        const data = await response.json();

        if (data.token) {
          // Store only non-sensitive data for UI and scheduling
          if (data.expires_at) {
            localStorage.setItem('lawd_expires_at', String(data.expires_at));
          }
          if (data.user) {
            localStorage.setItem('lawd_user', JSON.stringify(data.user));
          }
          log('Token refreshed OK. New expiry:', new Date(data.expires_at * 1000).toLocaleString());
          scheduleProactiveRefresh();
          return true;
        }

        warn('Refresh response missing token field');
        return false;
      } catch (error) {
        warn(`Token refresh network error (attempt ${attempt + 1}):`, error.message);
        if (attempt < MAX_RETRIES - 1) {
          log(`Network error — retrying in ${RETRY_DELAYS[attempt]}ms...`);
          await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt]));
          continue;
        }
        warn('All refresh retries exhausted');
        return false;
      }
    }
    return false;
  })().finally(() => { refreshPromise = null; });

  return refreshPromise;
};

/**
 * Schedule a proactive token refresh before expiry.
 */
const scheduleProactiveRefresh = () => {
  if (refreshTimer) clearTimeout(refreshTimer);

  const expiresAt = localStorage.getItem('lawd_expires_at');
  if (!expiresAt) return;

  const expiresMs = Number(expiresAt) * 1000;
  const now = Date.now();
  const refreshIn = Math.max((expiresMs - now) - REFRESH_MARGIN_MS, 10000);

  log(`Proactive refresh scheduled in ${Math.round(refreshIn / 1000)}s (token expires ${new Date(expiresMs).toLocaleString()})`);

  refreshTimer = setTimeout(() => {
    log('Proactive refresh timer fired');
    refreshAccessToken();
  }, refreshIn);
};

// Start proactive refresh on module load if we have an expiry time
if (typeof window !== 'undefined' && localStorage.getItem('lawd_expires_at')) {
  scheduleProactiveRefresh();
}

// === CROSS-TAB SCHEDULING SYNC ===
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'lawd_expires_at' && e.newValue) {
      log('Expiry updated by another tab — re-scheduling proactive refresh');
      scheduleProactiveRefresh();
    }
  });
}

// Refresh on tab visibility change (handles browser sleep/background)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const expiresAt = localStorage.getItem('lawd_expires_at');
      if (expiresAt) {
        const timeLeft = Number(expiresAt) * 1000 - Date.now();
        if (timeLeft < REFRESH_MARGIN_MS) {
          log(`Tab visible — token ${timeLeft < 0 ? 'EXPIRED' : 'expiring soon'}, refreshing now`);
          refreshAccessToken();
        } else {
          log(`Tab visible — token OK (${Math.round(timeLeft / 60000)}min left), re-scheduling`);
          scheduleProactiveRefresh();
        }
      }
    }
  });
}

/**
 * Handle 401 response - attempt token refresh and retry request.
 */
const handle401 = async (endpoint, options) => {
  log(`Got 401 on ${endpoint} — attempting refresh`);
  const success = await refreshAccessToken();

  if (success) {
    const url = `${API_BASE}/api${endpoint}`;
    return fetch(url, { ...options, credentials: 'include' });
  }

  warn(`Refresh failed for 401 on ${endpoint}`);
  return null;
};

// Standard JSON API call with httpOnly cookie auth
export const api = async (endpoint, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  const url = `${API_BASE}/api${endpoint}`;
  let response = await fetch(url, { ...options, headers, credentials: 'include' });

  // Handle 401 - attempt token refresh (but NOT for auth endpoints)
  if (response.status === 401 && !endpoint.startsWith('/auth/') && !endpoint.startsWith('/owner-driver/register')) {
    const retryResponse = await handle401(endpoint, { ...options, headers });
    if (retryResponse) {
      response = retryResponse;
    }
  }

  if (!response.ok) {
    let errorMsg = `HTTP ${response.status}`;
    try {
      const errorData = await response.clone().json();
      const detail = errorData.detail || errorData.message;
      if (Array.isArray(detail)) {
        errorMsg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
      } else {
        errorMsg = detail || errorMsg;
      }
    } catch (e) {
      // If JSON fails, the response might be text or empty
    }
    throw new Error(errorMsg);
  }

  try {
    return await response.json();
  } catch (e) {
    return {};
  }
};

// Blob API call (for file downloads: CSV, PDF, etc.) with httpOnly cookie auth
export const apiBlob = async (endpoint, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  const url = `${API_BASE}/api${endpoint}`;
  let response = await fetch(url, { ...options, headers, credentials: 'include' });

  // Handle 401 - attempt token refresh
  if (response.status === 401) {
    const retryResponse = await handle401(endpoint, { ...options, headers });
    if (retryResponse) {
      response = retryResponse;
    }
  }

  if (!response.ok) {
    let errorMsg = `HTTP ${response.status}`;
    try {
      const errData = await response.clone().json();
      errorMsg = errData.detail || errData.message || errorMsg;
    } catch (e) { /* response not JSON */ }
    throw new Error(errorMsg);
  }

  return response.blob();
};

// FormData API call (for file uploads) with httpOnly cookie auth
export const apiFormData = async (endpoint, formData) => {
  const url = `${API_BASE}/api${endpoint}`;
  let response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData
  });

  // Handle 401 - attempt token refresh
  if (response.status === 401) {
    const retryResponse = await handle401(endpoint, { method: 'POST', body: formData });
    if (retryResponse) {
      response = retryResponse;
    }
  }

  if (!response.ok) {
    let errorMsg = `HTTP ${response.status}`;
    try {
      const errData = await response.clone().json();
      errorMsg = errData.detail || errData.message || errorMsg;
    } catch (e) { /* response not JSON */ }
    throw new Error(errorMsg);
  }

  try {
    return await response.json();
  } catch (e) {
    return {};
  }
};

// Simple GET without auth (for public endpoints)
export const apiPublic = async (endpoint) => {
  const url = `${API_BASE}/api${endpoint}`;
  const response = await fetch(url);

  if (!response.ok) {
    let errorMsg = `HTTP ${response.status}`;
    try {
      const errData = await response.clone().json();
      errorMsg = errData.detail || errData.message || errorMsg;
    } catch (e) { /* response not JSON */ }
    throw new Error(errorMsg);
  }

  try {
    return await response.json();
  } catch (e) {
    return {};
  }
};
